/**
 * 周期规则内核 —— **F 域 F1 的唯一口径实现**（纯函数 · 零 I/O · 零时钟）
 *
 * 依据：规格 **§18**（周期记账 / 计划）· `DR-010`（周期与现金流口径定稿）·
 * F 域 PRD **§4.1 / §4.2 / §4.3 / §4.4 / §4.8** · 架构 **§4.2 E 段**（`schedules`）· `AC-F1.1~F1.23`。
 *
 * ## 本文件解决的四件事（各对应一处"最容易做错"）
 *
 * | # | 事项 | 最容易做错的做法 |
 * |:--:|---|---|
 * | 1 | **月末 clamp**（`AC-F1.7`） | 用 `addMonths(上一期, 1)` 直接推进 —— 它按**上一期的日**clamp ⇒ 31 日规则在 2 月生成后变成 28 日，**之后永远是 28 日**（3 月 28、4 月 28…），`AC-F1.7` 的「遇 4 月 → 4/30」不成立 |
 * | 2 | **锚点日从哪来** | `schedules` **没有**「起始日 / 锚点日」列（架构 §4.2 只列关键字段，v1 的 `start_date` 未迁入）⇒ 锚点只能从**已有列**恢复（见下节） |
 * | 3 | **期次序号** | v1 的 `index` 是"从首个期次数的稠密序号"，需要**规则首期**才能算；而我们**不存首期** ⇒ 改用**稳定且无需历史**的取值（见 `periodIndexOf`） |
 * | 4 | **形态判定** | 把"kind 与账户品类必须配套"写在服务层 ⇒ 端侧与写库前**各判一遍**（铁律 24 的现场）。⇒ 判定只在本文件，服务端与端侧**转发同一条中文文案**（㉛ 批的「对字」做法） |
 *
 * ## 🔴 锚点日恢复规则（`anchorDayOf`）—— 为什么可以只用两个日期算出来
 *
 * **前提**：clamp 只会把日号**降低**（31 → 28/30），**永不抬高**；且被写下的每个到期日 ≤ 锚点日。
 *
 * **恢复规则**：`锚点日 = max(day(下一期到期日), day(上一期已生成日))`。
 *
 * **为什么成立**（对 `monthly` / `quarterly`）：
 * - 锚点日**在含该日的每个自然月里被原样写下** ⇒ 任何一次推进，**相邻两期至少有一个携带锚点日**；
 * - 而"不含该日"的月份只有 2 月（缺 29/30/31）与 4/6/9/11 月（缺 31）——
 *   **`monthly` 下不可能连续两期都落在缺日的月份**（2 月不与 2 月相邻）；
 *   **`quarterly` 下窗口是 Jan/Apr/Jul/Oct** ⇒ 其中只有 **4 月**缺 31，同样不会连续出现。
 * - ⇒ 相邻两期的日号最大值 = 锚点日，`max` 是**恒等恢复**，不是近似。
 *
 * ⚠️ **已知边界（显式登记，见 `docs/changelog/2026-10-07-f-schedule.md`）**：
 * **`yearly` 且锚点日 = 2/29** 时，连续两个非闰年（2/28、2/28）会让锚点衰减为 28，
 * 之后遇闰年不会回到 2/29。根因 = **锚点没有独立载体**（`schedules` 缺「起始日」列）。
 * ⇒ 若产品要求严格正确，须给 `schedules` 加一列（`startDate` / `anchorDay`）——
 * **属 schema 变更，须走 DR**，已登记为交接文档的待确认项，**本批不改**。
 *
 * ## 🔴 期次序号（`periodIndexOf`）
 *
 * 幂等键是 `(sourceRuleId, sourcePeriodIndex)`（规格 §18 · `AC-F1.6`），
 * 它要的是「**同一规则的同一期次 → 同一个数**」，而**不是**"必须是 0,1,2,…"。
 * v1 的稠密序号需要"规则首期"这个我们并不保存的量 ⇒ 取
 * **`YYYYMMDD` 的十进制整数**（`2026-02-28` ⇒ `20260228`）：
 * 单调、一期一号、**无需任何历史**、跨停机稳定。
 */

import { AccountType, ContraKind, InternalCategory } from '../enums/account.js';
import { ScheduleFrequency, ScheduleKind } from '../enums/plan.js';
import { addDays, clampInt, fromYmd, lastDayOfMonth, parseDay, addMonths } from './calendar.js';

// ─────────────────────────── 形态判定（唯一实现） ───────────────────────────

/** 规则形态判定入参 —— 与「是否已绑定」一起判，避免服务层与端侧各判一半 */
export interface ScheduleShapeInput {
  kind: ScheduleKind;
  frequency: ScheduleFrequency;
  accountType: AccountType;
  /** 账户的 `internal` 品类；非 `internal` 类型为 `null` */
  accountCategory: InternalCategory | null;
  /** 账户的 `contra` 事项类型；非 `contra` 类型为 `null`（`DR-044` D7：`loan` 规则可绑 `contra` 贷款） */
  contraKind: string | null;
  /** 该账户**是否已绑定另一条规则**（查询由调用方完成，判定归本文件） */
  alreadyBound: boolean;
  /** 整数分（**可为负** —— 见 `directionOfAmount`） */
  amount: number;
}

/** 每种 `kind` 允许绑定的账户形态（`AC-F1.3` / `AC-F1.4` / `AC-F1.5`） */
const KIND_ACCOUNT: Readonly<Record<string, { type: AccountType; category: InternalCategory | null }>> = {
  [ScheduleKind.Recurring]: { type: AccountType.Internal, category: InternalCategory.Payable },
  [ScheduleKind.Amortization]: { type: AccountType.Internal, category: InternalCategory.Amortize },
};

/**
 * 🔴 **该 `kind` 是否写「账户 : 规则 = 1:1」这条从属关系** —— 唯一判定处（`DR-074` `D4`）。
 *
 * | `kind` | 1:1 | 为什么 |
 * |:--:|:--:|---|
 * | `recurring` / `amortization` / `loan` | ✅ | 规则是**账户的从属**（待缴 / 待摊 / 贷款账户由规则驱动）⇒ 规格 §40 的双载体（`schedules.accountId` + `accounts.scheduleRuleId`）必须同时有值 |
 * | 🆕 `template` | ❌ **1:N** | 一张信用卡会同时扣保险、会员费、话费 ⇒ 写 `accounts.scheduleRuleId` 会造出"账户指向某一条模板"的**假从属**，并让第二条模板**覆盖**第一条 |
 * | `installment` | ❌ | P1 才启用，且分期走 `installment_plans`（不在 `schedules`） |
 *
 * 🔴 **调用方必须读它、不要各写一遍 `kind !== 'template'`**：
 * `ScheduleService` 的 `create` / `remove` / `isBound` 三处共用本函数 ——
 * 三处各写一遍就是三份会分叉的判据（铁律 24）。
 */
export function bindsAccountOneToOne(kind: ScheduleKind): boolean {
  return kind === ScheduleKind.Recurring || kind === ScheduleKind.Amortization || kind === ScheduleKind.Loan;
}

/**
 * 周期规则形态校验 —— **唯一判定处**（服务端写库前调用，端侧转发同一文案）。
 *
 * @returns `null` = 通过；否则为**可直接展示的中文错误文案**（服务端 `400` 的 `message`）
 */
export function checkScheduleShape(input: ScheduleShapeInput): string | null {
  // ① `installment` 延 P1（`AC-F1.1`）—— 枚举值先定义但 P0 不得产生该值
  if (input.kind === ScheduleKind.Installment) {
    return '信用卡分期（installment）延后到 P1，本版不支持。';
  }

  // 🔴 `template`（`DR-074` D3）：**不判账户品类**，只判「账户存在 + 金额非 0」
  //    —— 下面 ②（预付卡）与 KIND_ACCOUNT（kind ↔ 品类配套）约束的都是**账户从属型**规则
  //    （`AC-F1.5` 的语境是"待缴 / 待摊账户能不能挂规则"）。模板的账户是"钱真的进出的那个账户"，
  //    品类与规则语义无关 ⇒ 储蓄卡 / 信用卡 / 电子钱包 / 社保公积金 / 投资账户都该能用。
  //    ⚠️ 账户存在性由调用方 `accounts.detail()` 保证（查不到即 404），本函数不看库。
  if (input.kind === ScheduleKind.Template) {
    return checkScheduleAmount(input.kind, input.amount);
  }

  // ② 预付卡无周期规则（`AC-F1.5`）—— 它虽是 `internal`，但品类不允许
  if (input.accountType === AccountType.Internal && input.accountCategory === InternalCategory.Prepaid) {
    return '预付卡账户不支持周期规则。';
  }

  // ③ kind 与账户形态必须配套（`AC-F1.3` / `AC-F1.4`）
  if (input.kind === ScheduleKind.Loan) {
    // 🔴 `loan` 规则的绑定形态（`DR-044` D7 拍板）：参数载体在**两类贷款账户**上 ——
    // ① `type='loan'`（机构贷款专类）；② `type='contra'` 且 `contraKind='loan'`（借贷域载体，
    // 六列参数 `annualRateE4` / `terms` / `startDate` / `method` / `subtype` 只在这类账户上有值）。
    // 旧判定只认 ① ⇒ 迁移来的房贷（②）永远绑不上规则，F 域生成器无从开工 —— 本判定即批 80 的解锁点。
    const isLoanAccount =
      input.accountType === AccountType.Loan ||
      (input.accountType === AccountType.Contra && input.contraKind === ContraKind.Loan);
    if (!isLoanAccount) {
      return '贷款计划只能绑定贷款账户（type=loan，或借贷账户且事项为贷款）。';
    }
    // 🔴 计划表按月推进（`loanDueDates` 的月序）⇒ 频率必须为按月（其余档位无计划表语义）
    if (input.frequency !== ScheduleFrequency.Monthly) {
      return '贷款计划的频率必须为按月。';
    }
  } else {
    const want = KIND_ACCOUNT[input.kind];
    if (want === undefined) {
      return `未知的周期规则种类：${String(input.kind)}。`;
    }
    if (input.accountType !== want.type || (want.category !== null && input.accountCategory !== want.category)) {
      return '周期规则只能绑定待缴账户（周期账）、待摊账户（摊销）或贷款账户（贷款计划）。';
    }
  }

  // ④ 账户 : 规则 = 1:1（`AC-F1.3` / `AC-F1.4`；唯一索引兜底）
  //    🔴 只对**账户从属型**规则成立（`DR-074` D2/D4）：`template` 是 1:N
  if (bindsAccountOneToOne(input.kind) && input.alreadyBound) {
    return '该账户已绑定一条周期规则（账户与规则一一对应）。';
  }

  // ⑤ 金额（整数分 / 非 0 / 待摊与贷款只正）—— 判定只在 `checkScheduleAmount` 一处
  return checkScheduleAmount(input.kind, input.amount);
}

/**
 * **金额段**校验 —— `checkScheduleShape` 的第 ⑤ 步，独立导出给「改规则」复用。
 *
 * 🔴 为什么要有这个独立入口（批 `131`）：`PATCH /api/schedules/:id` **不允许改账户与 `kind`**
 * （见 `ScheduleService.update`）⇒ 账户形态不可能变，编辑时再判「kind ↔ 账户配套」是
 * **无意义的重判**：v1 迁移来的历史规则，账户类型本就不配套、甚至压根没绑账户，
 * 用户**没有任何办法修正那个账户**（契约里没有这个字段）⇒ 结果是一条都改不动，
 * 报的还是「账户不存在或不属于当前账本」这种**指向错误的文案**。
 * 金额相反 —— 它是编辑的输入，必须判。
 *
 * ⇒ 拆成两个入口，而 `checkScheduleShape` 内部**调用本函数**（金额判定仍只有这一份实现，
 * 不违反「一个机制一个归属」）。
 */
export function checkScheduleAmount(kind: ScheduleKind, amount: number): string | null {
  if (!Number.isInteger(amount)) {
    return '周期金额必须是整数分。';
  }
  if (amount === 0) {
    return '周期金额不能为 0。';
  }
  // 待摊 / 贷款**只正**；`recurring` / `template` 可正可负
  // （补贴、公积金入账 ⇒ 方向为 `in`；模板承载的正是「每月固定入账」这类）
  if (kind !== ScheduleKind.Recurring && kind !== ScheduleKind.Template && amount < 0) {
    return '待摊与贷款计划的金额必须大于 0。';
  }
  return null;
}

/**
 * 金额 → 生成腿的方向（`recurring` / `template` 的「**可正可负**」，规格 §18 原文）。
 *
 * 🔴 **正负落在方向上，不落在金额上**：腿金额一律取绝对值、方向由符号决定 ⇒
 * 下游（`composeExpense` / 余额派生）拿到的永远是"合法腿"，**不需要理解负数金额**。
 * 待摊（`amortization`）与贷款（`loan`）的金额由 `checkScheduleAmount` 限制为正 ⇒ 恒为 `out`。
 */
export function directionOfAmount(amount: number): 'in' | 'out' {
  return amount < 0 ? 'in' : 'out';
}

// ─────────────────────────── 锚点日与推进 ───────────────────────────

/**
 * 从两个已有日期恢复**锚点日**（= 规则「每月几号」的那个号）。
 *
 * 规则与证明见文件头「锚点日恢复规则」；本函数是它的唯一实现。
 *
 * @param nextDueDay  `schedules.nextRunAt` 的账本本地日（`YYYY-MM-DD`）；未设置为 `null`
 * @param lastDoneDay `schedules.lastTickAt` 的账本本地日（水位）；从未生成过为 `null`
 * @returns 锚点日（1–31）；两个入参都为空时返回 `null`（无法恢复 ⇒ 调用方应视为"规则未就绪"）
 */
export function anchorDayOf(nextDueDay: string | null, lastDoneDay: string | null): number | null {
  const a = nextDueDay === null ? null : parseDay(nextDueDay).d;
  const b = lastDoneDay === null ? null : parseDay(lastDoneDay).d;
  if (a === null && b === null) return null;
  if (a === null) return b;
  if (b === null) return a;
  return a > b ? a : b;
}

/**
 * 推进**一期**（`AC-F1.2` / `AC-F1.7`）。
 *
 * 🔴 月末 clamp 打的是**锚点日**，不是 `from` 的日号 —— 这是与 `calendar.addMonths(from, n)`
 * 的唯一区别（后者按 `from` 的日 clamp ⇒ 会漂移）。
 */
export function nextDueDay(anchorDay: number, frequency: ScheduleFrequency, from: string): string {
  let step: number;
  switch (frequency) {
    case ScheduleFrequency.Daily:
      return addDays(from, 1);
    case ScheduleFrequency.Weekly:
      return addDays(from, 7);
    case ScheduleFrequency.Monthly:
      step = 1;
      break;
    case ScheduleFrequency.Quarterly:
      step = 3;
      break;
    case ScheduleFrequency.Yearly:
      step = 12;
      break;
    default:
      // 🔴 形态不认识就报错（不静默按某一档处理 —— 那是"越用越漏"的经典成因）
      throw new Error(`未知的周期频率：${String(frequency)}`);
  }
  // 用 `addMonths` 求**目标年月**（月算术唯一实现），日号由锚点日 + 该月最后一天决定
  const probe = parseDay(addMonths(from, step));
  return fromYmd(probe.y, probe.m, clampInt(anchorDay, 1, lastDayOfMonth(probe.y, probe.m)));
}

/** {@link dueDaysThrough} 的入参 */
export interface DueDaysInput {
  anchorDay: number;
  frequency: ScheduleFrequency;
  /** 序列起点（**含**）—— 生产路径传 `schedules.nextRunAt` 的本地日 */
  firstDueDay: string;
  /** 处理到的**账本本地日**（含）—— 由调用方按账本时区算好（内核不做时区） */
  through: string;
  /** 单次最多推进几期（安全阀，见 `SchedulePolicy.MaxCatchUpPeriods`） */
  maxPeriods: number;
}

/**
 * 列出 `[firstDueDay, through]` 区间内**全部应生成的期次**（水位补跑，`AC-F1.11`）。
 *
 * 生产路径：`firstDueDay` = 规则存着的 `nextRunAt`（**不是**"今天"）⇒
 * 停机 N 天重启后，把落下的期次**逐期补齐**（v1 只补一次 ⇒ 停机超窗会漏期）。
 * 幂等由 `(sourceRuleId, sourcePeriodIndex)` 唯一索引兜底，故**重复调用安全**。
 */
export function dueDaysThrough(input: DueDaysInput): string[] {
  const out: string[] = [];
  if (input.maxPeriods <= 0) return out;
  let cur = input.firstDueDay;
  // 防御：起点本身晚于窗口 ⇒ 无期次
  if (cur > input.through) return out;
  for (let i = 0; i < input.maxPeriods; i++) {
    if (cur > input.through) break;
    out.push(cur);
    cur = nextDueDay(input.anchorDay, input.frequency, cur);
  }
  return out;
}

/**
 * 稳定期次序号（幂等键的第二半，`AC-F1.6`）—— `YYYY-MM-DD` ⇒ `YYYYMMDD` 整数。
 *
 * 语义："同一规则的同一期次恒得同一个数"（**不要求**从 0 开始稠密编号，见文件头）。
 */
export function periodIndexOf(dueDay: string): number {
  const { y, m, d } = parseDay(dueDay);
  return y * 10000 + m * 100 + d;
}

/**
 * 规则的"下一期"是否已到期（门闸，F 域 PRD §4.1.2：`nextRunAt > now` 时跳过）。
 *
 * @param nextDueDay 规则存着的 `nextRunAt` 本地日（`null` = 规则未就绪 ⇒ **不生成**）
 * @param today      账本本地「今天」（`YYYY-MM-DD`）
 */
export function isDue(nextDueDay: string | null, today: string): boolean {
  return nextDueDay !== null && nextDueDay <= today;
}
